import { OverridesCard } from "../../../../../../../components/general_tab/overrides_card";
import * as React from "react";
import AlertCard from "../../../../../../../components/alerting/alert_card";
import { useParams } from "react-router-dom";
import useBackend, { useUserPrivilegeKeyPermissionsMap } from "../../../../../../../hooks/use_backend";
import { useQuery } from "@tanstack/react-query";
import { REACT_QUERY_KEYS } from "../../../../../../../enums/react_query_key_enum";
import {
  fetchImbalancePriceEpex,
  fetchMeteringActuals,
  fetchMeteringHistorical,
} from "../../../../../../../scripts/api_v2/metering_client";
import { Box, Button, CircularProgress, IconButton } from "@mui/material";
import { fetchSiteOverridesHistorical } from "../../../../../../../scripts/api_v2/overrides_client";
import { fetchSiteAlertsHistorical } from "../../../../../../../scripts/api_v2/alerting_client";
import { addDays } from "date-fns";
import { GPCCard } from "../../../../../../../components/general_tab/gpc_card";
import { isCompleted, renderApprovalButtons } from "../../enum/commissioning_required_action";
import { FINAL_VERIFICATION_ACTIONS } from "../../enum/final_verification_actions";
import DialogForm from "../../../../../../../components/material_react_table/dialog_form";
import { FORM_ACTIONS } from "../../../../../../../enums/form_actions_enum";
import { useState } from "react";
import MultiTabDialog from "../../../../../../../components/material_react_table/multi_tab_dialog";
import CommissioningVerificationGeneralData from "./commissioning_verification_general_data";
import ReviewTimelineCard from "./commissioning_review_timeline";
import AddCommentIcon from "@mui/icons-material/AddComment";
import { LynxMetering } from "../../../../../../../components/commissioning_tab/lynx_metering";
import CommissioningMetering from "../../../../../../../components/commissioning_tab/commissioning_metering";

export const CommissioningReportComponent = ({
  isLocked,
  isAdmin,
  hasMetering,
  mutation,
  isMutating,
  step,
  requiredPrivilege,
}) => {
  const params = useParams();
  const backend = useBackend();
  const permissionMap = useUserPrivilegeKeyPermissionsMap();
  const verificationIsCommissioned = isCompleted(step.required_action);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [dialogTabs, setDialogTabs] = useState(null);
  const [includeMetering, setIncludeMetering] = useState(false);

  const commissioningControlStartDate = new Date(step.valid_from_utc * 1000);
  const commissioningControlEndDate =
    step.valid_to_utc != null ? new Date(step.valid_to_utc * 1000) : addDays(commissioningControlStartDate, 1);
  const commissioningCreatedDate = new Date(step.date_created * 1000);

  let { data: meteringActuals, isLoading: fetchingActuals } = useQuery({
    queryKey: [REACT_QUERY_KEYS.METERING_ACTUALS, backend.fetch, params.site_id, commissioningControlStartDate],
    queryFn: () => fetchMeteringActuals(backend.fetch, params.site_id, commissioningControlStartDate, 0, 1),
    refetchInterval: 30000,
  });

  let { data: meteringHistorical, isLoading: fetchingMetering } = useQuery({
    queryKey: [REACT_QUERY_KEYS.METERING_HISTORICAL, backend.fetch, params.site_id, commissioningControlStartDate],
    queryFn: () => fetchMeteringHistorical(backend.fetch, params.site_id, commissioningControlStartDate, 0, 1),
    refetchInterval: 40000,
  });

  let { data: imbalancePriceEpex, isLoading: fetchingPrices } = useQuery({
    queryKey: [REACT_QUERY_KEYS.IMBALANCE_EPEX, backend.fetch, commissioningControlStartDate],
    queryFn: () => fetchImbalancePriceEpex(backend.fetch, commissioningControlStartDate, 0, 1),
    refetchInterval: 900000,
  });

  let {
    data: overrides,
    isLoading: fetchingOverrides,
    error: overridesError,
  } = useQuery({
    queryKey: [
      REACT_QUERY_KEYS.COMMISSIONING_REPORT_OVERRIDES,
      backend.fetch,
      params.site_id,
      commissioningCreatedDate,
      commissioningControlEndDate,
    ],
    queryFn: () =>
      fetchSiteOverridesHistorical(
        backend.fetch,
        params.site_id,
        commissioningCreatedDate,
        commissioningControlEndDate,
      ),
    refetchInterval: 40000,
  });

  let { data: alerts, isLoading: fetchingAlerts } = useQuery({
    queryKey: [
      REACT_QUERY_KEYS.COMMISSIONING_REPORT_ALERTS,
      backend.fetch,
      params.site_id,
      commissioningCreatedDate,
      commissioningControlEndDate,
    ],
    queryFn: () =>
      fetchSiteAlertsHistorical(backend.fetch, params.site_id, commissioningCreatedDate, commissioningControlEndDate),
    refetchInterval: 40000,
  });

  function handleDenyReason() {
    setDialogTabs([
      {
        label: "Enter deny reason",
        component: (
          <DialogForm
            selectedRow={{}} // Pass selected row data
            onClose={() => setIsDialogOpen(false)} // Close the dialog
            dialogAction={FORM_ACTIONS.COMMISSIONING_DENY_REASON} // Your action
            handleConfirm={handleConfirmDenyReason}
            isPending={isMutating}
          />
        ),
      },
    ]);
    setIsDialogOpen(true); // Open the dialog
  }

  function handleComments() {
    setDialogTabs([
      {
        label: "Handle comments",
        component: (
          <DialogForm
            selectedRow={{}} // Pass selected row data
            onClose={() => setIsDialogOpen(false)} // Close the dialog
            dialogAction={FORM_ACTIONS.COMMISSIONING_COMMENTS} // Your action
            handleConfirm={handleConfirmComments}
            isPending={isMutating}
          />
        ),
      },
    ]);
    setIsDialogOpen(true); // Open the dialog
  }

  const handleConfirmComments = async (selectedRow, dialogAction, dialogData) => {
    mutation({
      item_id: step.step_id,
      item_value: true,
      action: FINAL_VERIFICATION_ACTIONS.ADD_COMMENTS,
      comments: dialogData["comments"],
    });
    setIsDialogOpen(false);
  };

  const handleConfirmDenyReason = async (selectedRow, dialogAction, dialogData) => {
    mutation({
      item_id: step.step_id,
      item_value: true,
      action: FINAL_VERIFICATION_ACTIONS.RESET_CONTROL,
      comments: dialogData["deny_reason"],
    });
    setIsDialogOpen(false);
  };

  function getAddCommentButton() {
    if (isAdmin) {
      return (
        <IconButton
          color="inherit"
          size="small"
          disabled={!isAdmin}
          onClick={() => {
            handleComments();
          }}
          sx={{
            cursor: "pointer",
          }}
        >
          <AddCommentIcon />
        </IconButton>
      );
    } else {
      return null;
    }
  }

  return (
    <div style={{ padding: "10px" }}>
      <div>
        <ReviewTimelineCard
          rightAdornment={getAddCommentButton()}
          startCollapsed={!Array.isArray(step.changelog) || step.changelog.length === 0}
          changelog={Array.isArray(step.changelog) && step.changelog.length > 0 ? step.changelog : []}
        />
      </div>
      <div style={{ marginTop: 10 }}>
        <CommissioningVerificationGeneralData
          commissioningCreatedDate={commissioningCreatedDate}
          commissioningControlStartDate={commissioningControlStartDate}
          commissioningControlEndDate={commissioningControlEndDate}
          commissioned_device_names={step.commissioned_device_names}
        />
      </div>
      <div>
        <OverridesCard
          startCollapsed={verificationIsCommissioned}
          overrides={overrides}
          isLoading={fetchingOverrides}
          error={overridesError}
          sx={{ mt: 2 }}
          canEdit={isAdmin}
        />
        <AlertCard
          startCollapsed={verificationIsCommissioned}
          isLoading={fetchingAlerts}
          alerts={alerts}
          sx={{ mt: 2 }}
        />
        <CommissioningMetering
          sx={{ mt: 2 }}
          startCollapsed={verificationIsCommissioned}
          metering={meteringHistorical?.metering}
          actuals={meteringActuals}
          fetchingMetering={fetchingMetering}
          fetchingActuals={fetchingActuals}
          fetchingPrices={fetchingPrices}
          prices={imbalancePriceEpex?.imbalanceprice_epex}
          isCommissioningReport={true}
        />
        <LynxMetering sx={{ mt: 2 }} startCollapsed={verificationIsCommissioned} />
      </div>
      <div>
        <GPCCard
          startCollapsed={verificationIsCommissioned}
          permissionMap={permissionMap}
          isEditable={false}
          required_privilege={requiredPrivilege}
          sx={{ mt: 2 }}
        />
      </div>
      {isMutating ? (
        <Box display="flex" justifyContent="center" sx={{ mb: 2, mt: 5 }}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <Box display="flex" justifyContent="center" sx={{ mb: 2, mt: 5, gap: 10 }}>
          <Box sx={{ display: "flex", flexDirection: "row", flex: 1, gap: 1 }}>
            <Button
              variant="outlined"
              color="inherit"
              size="small"
              disabled={isLocked}
              onClick={() => {
                if (isAdmin) {
                  handleDenyReason();
                } else {
                  mutation({
                    item_id: step.step_id,
                    item_value: true,
                    action: FINAL_VERIFICATION_ACTIONS.RESET_CONTROL,
                  });
                }
              }}
              sx={{ borderRadius: 2 }}
              disableElevation
            >
              {"Reset final verification"}
            </Button>
            <MultiTabDialog open={isDialogOpen} onClose={() => setIsDialogOpen(false)} dialogTabs={dialogTabs} />
          </Box>
          {renderApprovalButtons(
            step,
            mutation,
            !(isAdmin && !isLocked),
            hasMetering,
            includeMetering,
            setIncludeMetering,
          )}
        </Box>
      )}
    </div>
  );
};

export default CommissioningReportComponent;
